import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Button, Grid, IconButton, Pagination, Stack, TablePagination, TextField, Typography } from '@mui/material';
import config from 'src/config';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import showLoading from 'src/common/loading';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { getContainers } from 'src/services/container.service';
import ContainerCardComponent from '../../components/containers/ContainerCard.component';
import AddEditContainerModal from './AddEdit.modal';

// ----------------------------------------------------------------------

const containerTypeMap = {
    'container': {
        name: 'Group',
        name_plural: 'Groups'
    },
    'department': {
        name: 'Department',
        name_plural: 'Departments'
    },
    'team': {
        name: 'Team',
        name_plural: 'Teams'
    },
    'project': {
        name: 'Project',
        name_plural: 'Projects'
    }
}


class ContainersListOnly extends React.Component {

    constructor(props) {
        super(props);

        this.state = {

            role: localStorage.getItem('role') || config.USER_ROLES.USER,

            showAddEditContainerModal: false,
            containers: [],
            type: this.props?.payload?.type || 'container',
            search: this.props?.payload?.search || '',
            sort_key: this.props?.payload?.sort_key || 'updated_at',
            sort_order: this.props?.payload?.sort_order || -1,
            recent_type: '',
            shared_by_me: false,
            shared_with_me: false,
            limit: 12,
            skip: 0,
            total_containers: 0,
        }
    }

    getMapValue = (key) => {
        return containerTypeMap[this.state.type][key] || '';
    }

    // promised setState
    setStateAsync(state) {
        return new Promise((resolve) => {
            this.setState(state, resolve)
        });
    }

    async componentDidMount() {
        await this.fetchContainers(false);
    }

    async componentDidUpdate(prevProps) {
        if (JSON.stringify(prevProps.payload) !== JSON.stringify(this.props.payload)) {
            await this.setStateAsync({
                type: this.props?.payload?.type || 'container',
                search: this.props?.payload?.search || '',
                sort_key: this.props?.payload?.sort_key || 'updated_at',
                sort_order: this.props?.payload?.sort_order || -1,
                recent_type: this.props?.payload?.recent_type || '',
                shared_by_me: this.props?.payload?.shared_by_me || false,
                shared_with_me: this.props?.payload?.shared_with_me || false,
                skip: 0,
                containers: this.props?.payload?.containers || []
            });
            await this.fetchContainers(false);
        }
    }

    fetchContainers = async(showLoader = true) => {
        try {

            if (showLoader)
                showLoading(true);


            const options = {
                limit: this.state.limit,
                skip: this.state.skip,
                sort_key: this.state.sort_key,
                sort_order: this.state.sort_order,
                type: this.state.type
            }

            if (this.state.search)
                options.search = this.state.search;

            if (this.state.recent_type)
                options.recent = this.state.recent_type;

            if (this.state.shared_by_me)
                options.shared_by_me = 'true';

            if (this.state.shared_with_me)
                options.shared_with_me = 'true';

            // if (this?.state?.containers?.length > 0) {
            //     options.containers = this.state.containers;
            // }

            const containers = await getContainers(options);
            if (containers.error) {

                showLoading(false);

                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: containers.error,
                    confirmButtonText: 'Ok',
                    confirmButtonColor: config.primaryColor
                });
                return;
            }

            this.setState({
                containers: containers.containers || [],
                total_containers: containers.count || 0
            });
            showLoading(false);

        } catch (error) {
            showLoading(false);
            console.error("Error fetching:", error.message)
        }
    }


    handleSearch = async (e) => {

        await this.setStateAsync({
            skip: 0,
            search: e.target.value
        })

        await this.fetchContainers(false);
    }

    render() {
        return (
            <>
                <Grid container spacing={2}>

                    {(this.state.containers.length === 0) ? (
                        <Grid item xs={12}>
                            <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', mb: 2, mt: 2, color: 'lightgray' }}>
                                No groups found. Try adjusting your search or filters.
                            </Typography>
                        </Grid>
                    ) : null}

                    {(this.state.containers.map((container) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={container._id}>
                            <ContainerCardComponent
                                container={container}
                                type={this.state.type}
                                fetchContainers={this.fetchContainers}
                                onContainerClick={async (container) => {
                                    if (this.props?.onContainerClick) {
                                        this.props.onContainerClick(container);
                                    }
                                }}
                            />
                        </Grid>
                    )))}
                </Grid>

                {(this.state.total_containers > 0) ? (
                    <Stack spacing={2} sx={{ alignItems: 'center', justifyContent:"flex-end" }} direction="row">
                        {(this.state.total_containers > 0) && (
                            <Typography variant="body2" color="text.disabled" component="div">
                                {`Total Groups: ${this.state.total_containers}`}
                            </Typography>
                        )}
                        <TablePagination
                            component="div"
                            count={this.state.total_containers}
                            page={Math.floor(this.state.skip / this.state.limit)} 
                            onPageChange={async (event, newPage) => {
                                await this.setState({ skip: newPage * this.state.limit });
                                await this.fetchContainers();
                            }}
                            rowsPerPage={this.state.limit}
                            rowsPerPageOptions={[]} 
                            sx={{
                                m: 2,
                                '& .MuiTablePagination-displayedRows': { color: 'text.disabled' },
                                '& .MuiTablePagination-selectLabel': { color: 'text.disabled' },
                                '& .MuiTablePagination-select': { color: 'text.disabled' },
                            }}
                        />
                    </Stack>
                ) : null}


                {(this.state.showAddEditContainerModal) ? (
                    <AddEditContainerModal
                        open={this.state.showAddEditContainerModal}
                        onClose={() => {
                            this.setState({
                                showAddEditContainerModal: false
                            });
                        }}
                        container={this.state.selectedContainer}
                        payload={{
                            type: this.state.type
                        }}
                        onSave={async () => {
                            await this.setStateAsync({
                                showAddEditContainerModal: false
                            });
                            await this.fetchContainers();
                        }}
                    />
                ) : null}

            </>
        );
    }
}

export default function (props) {
    const navigate = useNavigate();

    return <ContainersListOnly {...props} navigate={navigate} />;
}

const styles={
	fileNameStyles:{
		flex: 1,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	}
}